package com.sddamico.draftmachine.components.widgets

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.glowBrass
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

/**
 * The shared pressable-card container behind the wizard's selection surfaces (league rows, the
 * "spin up a new league" card, format options): brass border + tinted background when [selected],
 * a neutral border otherwise. [dashed] switches to a dashed border and drops the unselected
 * background/glow, for the "create new" card. [modifier] is applied last so a caller can override
 * padding, opacity, cursor, or the border outright (e.g. a disabled state).
 */
@Composable
fun SelectableCard(
  selected: Boolean,
  onClick: (() -> Unit)?,
  modifier: Modifier = Modifier,
  dashed: Boolean = false,
  ariaLabel: String? = null,
  content: @Composable () -> Unit,
) {
  val borderWidth = if (selected && !dashed) 2.px else 1.px
  val borderStyle = if (dashed) LineStyle.Dashed else LineStyle.Solid
  val borderColor = if (selected) DmColor.brass500 else DmColor.borderStrong
  val bg = if (selected) DmColor.brassTint(0.10f) else if (dashed) Colors.Transparent else DmColor.surfaceInset

  Div(
    attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(12.px).padding(14.px)
      .borderRadius(DmEffects.radiusMd)
      .border(borderWidth, borderStyle, borderColor)
      .backgroundColor(bg)
      .then(if (selected && !dashed) Modifier.glowBrass() else Modifier)
      .cursor(if (onClick != null) Cursor.Pointer else Cursor.Default)
      .then(modifier)
      .toAttrs {
        if (onClick != null) {
          attr("role", "button")
          if (ariaLabel != null) attr("aria-label", ariaLabel)
          onClick { onClick() }
        }
      }
  ) { content() }
}
